/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectIsLoggedIn,
  selectLogout,
} from 'app/providers/AuthProvider/slice/selectors';
import { Login } from './containers/Login/Loadable';
import { Portal } from './containers/Portal';
import { ForgotPassword } from './containers/ForgotPassword/Loadable';
import { ResetPassword } from './containers/ResetPassword/Loadable';
import { EnvironmentIndicator } from './containers/EnvironmentIndicator';
import { Box } from '@mui/material';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthPageContainer } from './components/AuthPageContainer';
import { LoadingIndicator } from './components/LoadingIndicator';
import { ManageAuth } from './containers/ManageAuth';
import { getToken } from '../utils/request';
import { useEffect, useState } from 'react';

export function App() {
  const loggedIn = useSelector(selectIsLoggedIn);
  const { loading: loggingOut } = useSelector(selectLogout);
  const [token, setToken] = useState(getToken());

  const defaultPage = '/portal';

  // Listen for changes to the token
  useEffect(() => {
    setToken(getToken());
    const handleStorageChange = () => {
      setToken(getToken());
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <Box sx={{ minWidth: 360 }}>
      <EnvironmentIndicator />
      <Helmet titleTemplate="%s - CAT" defaultTitle="CAT">
        <meta name="description" content="Customs Analytics Tool" />
      </Helmet>
      <Routes>
        <Route
          path={'/auth/manage/*'}
          element={
            <ProtectedRoute isAccessible={loggedIn} redirectToPath={'/'}>
              <ManageAuth />
            </ProtectedRoute>
          }
        />

        <Route
          path="/auth/*"
          element={
            <>
              <ProtectedRoute isAccessible={!loggedIn} redirectToPath={'/'}>
                {loggingOut ? (
                  <AuthPageContainer>
                    <LoadingIndicator />
                  </AuthPageContainer>
                ) : (
                  <>
                    <Routes>
                      <Route
                        path={'forgot-password'}
                        element={<ForgotPassword />}
                      />
                      <Route
                        path={'reset-password'}
                        element={<ResetPassword />}
                      />
                      <Route path={'*'} element={<Login />} />
                    </Routes>
                  </>
                )}
              </ProtectedRoute>
            </>
          }
        />
        <Route
          path={'/portal/*'}
          element={loggedIn ? <Portal /> : <Navigate to={'/auth/login'} />}
        />
        <Route path={'*'} element={<Navigate to={defaultPage} />} />
      </Routes>
    </Box>
  );
}
